import { GeonexAPIError } from 'flow/components/error-handling/GeonexAPIError';
import { regionCheckEndpoint } from './app-utils';
import { regions, sourceData } from 'flow/constants/region-lookup/login-data';
import {
    GeonexBrandingResponse,
    GeonexFeatureFlagsResponse,
    GeonexSessionResponse,
    GeonexUsernameRegionResponse,
} from 'flow/types/region-lookup/LoginData';
import {
    RegionDataType,
    RegionType,
    SocialLoginType,
} from 'flow/types/region-lookup/UserRegion';

const ALLOWED_HOST_PATTERNS = [
    /^https:\/\/[a-z0-9-]+\.telavox\.com$/,
    /^https:\/\/[a-z0-9-]+\.joinflow\.com$/,
    /^https:\/\/[a-z0-9-]+\.joinflow\.co\.uk$/,
    /^https:\/\/[a-z0-9-]+\.telavox\.se$/,
];

const VALID_REGIONS = new Set<string>(Object.values(regions));
const VALID_SOCIAL_LOGIN_TYPES = new Set<string>(Object.values(sourceData));

export const isAllowedUrl = (url: string): boolean =>
    ALLOWED_HOST_PATTERNS.some((pattern) => pattern.test(url));

const isValidSocialLoginType = (value: unknown): value is SocialLoginType =>
    typeof value === 'string' && VALID_SOCIAL_LOGIN_TYPES.has(value);

export const validateRegionData = (data: unknown): RegionDataType | null => {
    if (!data || typeof data !== 'object') return null;

    const {
        url,
        region,
        userName,
        lastUpdatedTime,
        successLoginCompleted,
        isOidcLogin,
        socialLoginType,
        socialSelectedUrl,
        displayName,
    } = data as Record<string, unknown>;

    if (typeof url !== 'string' || !isAllowedUrl(url)) return null;
    if (typeof region !== 'string' || !VALID_REGIONS.has(region)) return null;

    return {
        url,
        region: region as RegionType,
        userName: typeof userName === 'string' ? userName : undefined,
        lastUpdatedTime:
            typeof lastUpdatedTime === 'number' ? lastUpdatedTime : null,
        successLoginCompleted: !!successLoginCompleted,
        isOidcLogin: !!isOidcLogin,
        socialLoginType: isValidSocialLoginType(socialLoginType)
            ? socialLoginType
            : undefined,
        socialSelectedUrl:
            typeof socialSelectedUrl === 'string' &&
            isAllowedUrl(socialSelectedUrl)
                ? socialSelectedUrl
                : undefined,
        displayName: typeof displayName === 'string' ? displayName : undefined,
    };
};

const UNSAFE_PATH_CHARS = /[/\\?#]/;

const sanitizePathParam = (value: string): string | null => {
    if (UNSAFE_PATH_CHARS.test(value)) return null;
    return value;
};

export const getGeoNexBaseUrl = () =>
    process.env.NODE_ENV === 'development' ? '' : regionCheckEndpoint;

const FETCH_TIMEOUT_MS = 2000;

const createTimeoutSignal = (): AbortSignal => {
    if (typeof AbortSignal.timeout === 'function') {
        return AbortSignal.timeout(FETCH_TIMEOUT_MS);
    }

    const controller = new AbortController();
    setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    return controller.signal;
};

const fetchFromGeonex = async (
    path: string,
    errorContext: string,
    options?: RequestInit,
) => {
    try {
        const baseUrl = getGeoNexBaseUrl();
        const timeoutSignal = createTimeoutSignal();
        const signal =
            options?.signal && typeof AbortSignal.any === 'function'
                ? AbortSignal.any([options.signal, timeoutSignal])
                : timeoutSignal;
        const response = await fetch(`${baseUrl}${path}`, {
            ...options,
            signal,
        });
        if (!response.ok) {
            const errorBody = await response.text();
            throw new GeonexAPIError(
                response.status,
                response.statusText,
                errorBody,
                errorContext,
            );
        }
        return response.json();
    } catch (e) {
        if (e instanceof GeonexAPIError) {
            throw e;
        }
        const message = e instanceof Error ? e.message : String(e);
        const isTimeout =
            (e instanceof DOMException && e.name === 'TimeoutError') ||
            (e instanceof DOMException && e.name === 'AbortError');
        throw new GeonexAPIError(
            isTimeout ? 408 : 0,
            isTimeout ? 'Request Timeout' : 'Network Error',
            message,
            errorContext,
        );
    }
};

export const fetchBrandingFromGeonex = async (
    brandDomain: string,
): Promise<GeonexBrandingResponse | null> => {
    if (!brandDomain) return null;
    const safeDomain = sanitizePathParam(brandDomain);
    if (!safeDomain) return null;
    return fetchFromGeonex(`/api/branding/${safeDomain}`, 'branding');
};

export const fetchBrandingLogosFromGeonex = async (brandDomain: string) => {
    if (!brandDomain) return null;
    const safeDomain = sanitizePathParam(brandDomain);
    if (!safeDomain) return null;
    return fetchFromGeonex(`/api/branding/${safeDomain}/logo`, 'branding logo');
};

export const fetchRegionDataEndpoint = async (
    userName: string,
): Promise<GeonexUsernameRegionResponse | null> => {
    if (!userName) return null;
    const safeUserName = sanitizePathParam(userName);
    if (!safeUserName) return null;
    return fetchFromGeonex(
        `/api/region/lookup?username=${encodeURIComponent(safeUserName)}`,
        'region data',
    );
};

export const fetchAllRegions = async (): Promise<
    GeonexUsernameRegionResponse[] | null
> => {
    return fetchFromGeonex('/api/region', 'regions');
};

export const fetchFeatureFlagsFromGeonex =
    async (): Promise<GeonexFeatureFlagsResponse | null> => {
        return fetchFromGeonex('/api/feature-flags', 'feature flags');
    };

const LOGIN_NAMESPACE = 'login';
const REGION_SELECTOR = 'region_selector';

/**
 * Reads a flag out of the GET /api/feature-flags payload.
 *
 * Fail-closed: a missing namespace or a value that isn't literally `true`
 * reads as disabled, so an outage or a malformed payload degrades to the
 * feature being off rather than throwing on the boot path.
 */
const isGeonexFlagEnabled = (
    response: GeonexFeatureFlagsResponse | null | undefined,
    namespace: string,
    flag: string,
): boolean => response?.flags?.[namespace]?.[flag] === true;

/**
 * Whether Geonex says the region selector should be offered pre-login.
 */
export const isRegionSelectorEnabled = (
    response: GeonexFeatureFlagsResponse | null | undefined,
): boolean => isGeonexFlagEnabled(response, LOGIN_NAMESPACE, REGION_SELECTOR);

export const fetchInitialSessionFromGeonex = async (
    version: string,
    includes: string,
): Promise<GeonexSessionResponse | null> => {
    return fetchFromGeonex(
        `/api/session?version=${version}&includes=${includes}`,
        'session',
        { credentials: 'include' },
    );
};
